import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../AppIcon';

/**
 * Pagination.
 *
 * Nothing in this app paginated. Every table rendered every row it was given,
 * which is fine against seed data and not fine against a real conference —
 * a few hundred submissions means a few hundred DOM rows, each with buttons and
 * badges, built on every filter keystroke.
 *
 * Two pieces here, deliberately separate:
 *
 *   `usePagination(rows)` owns the slice and the page number. It resets to page
 *   one whenever the row count changes, because the alternative — filtering a
 *   list down to four results while sitting on page seven — shows an empty
 *   table and reads as "no results" when the results are simply elsewhere.
 *
 *   `<Pagination />` renders the control, and only when there's more than one
 *   page. A pager under a five-row table is noise.
 *
 * Page size lives with the caller, not here: a dense admin table and a list of
 * detail cards don't want the same number.
 */

export function usePagination(rows, pageSize = 25) {
  const [page, setPage] = useState(1);
  const total = rows?.length || 0;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));

  // Filtering usually shrinks the list. Staying on a page that no longer exists
  // shows an empty table, which is indistinguishable from "nothing matched".
  useEffect(() => {
    setPage((p) => Math.min(p, pageCount));
  }, [pageCount]);

  useEffect(() => {
    setPage(1);
  }, [total]);

  const pageRows = useMemo(() => {
    const start = (page - 1) * pageSize;
    return (rows || []).slice(start, start + pageSize);
  }, [rows, page, pageSize]);

  return {
    page,
    setPage,
    pageCount,
    pageRows,
    total,
    pageSize,
    from: total === 0 ? 0 : (page - 1) * pageSize + 1,
    to: Math.min(page * pageSize, total),
  };
}

/**
 * Page numbers with ellipses. Always shows first and last, the current page and
 * its neighbours — so the control keeps a stable width instead of growing with
 * the data.
 */
function pageList(page, pageCount) {
  if (pageCount <= 7) return Array.from({ length: pageCount }, (_, i) => i + 1);
  const out = [1];
  const left = Math.max(2, page - 1);
  const right = Math.min(pageCount - 1, page + 1);
  if (left > 2) out.push('…');
  for (let i = left; i <= right; i += 1) out.push(i);
  if (right < pageCount - 1) out.push('…');
  out.push(pageCount);
  return out;
}

const Pagination = ({ page, pageCount, setPage, from, to, total, className = '' }) => {
  const { t } = useTranslation('common');

  if (!pageCount || pageCount <= 1) return null;

  const go = (p) => setPage(Math.min(Math.max(1, p), pageCount));

  return (
    <nav
      aria-label={t('pagination.label', 'Pagination')}
      className={`flex items-center justify-between gap-3 px-4 py-2.5 border-t border-border ${className}`.trim()}
    >
      {/* Position, in words. "Page 3 of 12" alone doesn't say how much there is. */}
      <p className="text-xs text-muted-foreground tabular-nums">
        {t('pagination.showing', {
          from,
          to,
          total,
          defaultValue: '{{from}}–{{to}} of {{total}}',
        })}
      </p>

      <div className="flex items-center gap-1">
        <button
          onClick={() => go(page - 1)}
          disabled={page === 1}
          aria-label={t('pagination.previous', 'Previous page')}
          className="inline-flex items-center justify-center w-7 h-7 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors disabled:opacity-40 disabled:pointer-events-none"
        >
          {/* Logical property: the chevron must point "back", which flips in RTL. */}
          <Icon name="ChevronLeft" size={15} className="rtl:rotate-180" />
        </button>

        {pageList(page, pageCount).map((p, i) =>
          p === '…' ? (
            <span key={`gap-${i}`} className="px-1 text-xs text-muted-foreground select-none">
              …
            </span>
          ) : (
            <button
              key={p}
              onClick={() => go(p)}
              aria-current={p === page ? 'page' : undefined}
              className={`inline-flex items-center justify-center min-w-[1.75rem] h-7 px-1.5 rounded-md text-xs font-medium tabular-nums transition-colors ${
                p === page
                  ? 'bg-primary/10 text-primary'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted'
              }`}
            >
              {p}
            </button>
          )
        )}

        <button
          onClick={() => go(page + 1)}
          disabled={page === pageCount}
          aria-label={t('pagination.next', 'Next page')}
          className="inline-flex items-center justify-center w-7 h-7 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors disabled:opacity-40 disabled:pointer-events-none"
        >
          <Icon name="ChevronRight" size={15} className="rtl:rotate-180" />
        </button>
      </div>
    </nav>
  );
};

export default Pagination;
